/**
 * Root-scoped entry point every AGU surface records activity through.
 *
 * Owns one UserActivityQueue for the SPA's lifetime, the GraphQL transport, and
 * the two unload hooks (pagehide, visibilitychange→hidden) that flush whatever
 * is pending via keepalive. SessionID is minted once per tab so time-on-page
 * can later be derived from consecutive page views (spec §11).
 *
 * Deliberately knows nothing about surfaces, locations or DOM: the emitters
 * live in BaseAGUAppSurfaceComponent. No change-detection interaction (the
 * host is zoneless), no localStorage.
 */
import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { GraphQLUserActivityTransport } from './graphql-user-activity-transport';
import type { UserActivityEvent } from './user-activity-event';
import { UserActivityQueue } from './user-activity-queue';

@Injectable({ providedIn: 'root' })
export class UserActivityService {
    readonly SessionID: string = crypto.randomUUID();

    private readonly document = inject<Document>(DOCUMENT);
    private readonly queue = new UserActivityQueue(new GraphQLUserActivityTransport());

    constructor() {
        const view = this.document.defaultView;
        if (view) {
            view.addEventListener('pagehide', () => this.queue.FlushOnUnload());
        }
        this.document.addEventListener('visibilitychange', () => {
            if (this.document.visibilityState === 'hidden') this.queue.FlushOnUnload();
        });
    }

    Record(event: UserActivityEvent): void {
        this.queue.Record(event);
    }
}
